import { useState, useRef, useEffect, useMemo, Fragment } from 'react'
import { toast } from 'react-toastify'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { NavBar } from 'components/NavBar/NavBar'
import { NoData } from 'components/NoData'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { ColoredDot, Flex, H1WithBack, PageWrapper } from 'styling/GlobalStyles'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { MetricCard } from 'components/ui/MetricCard/MetricCard'
import {
    ActionsMenuWrapper,
    KebabIcon,
    ActionsDropdown,
    ActionsDropdownItem,
    PhaseList,
    PhaseItem,
    PhaseIndicator,
    PhaseDot,
    EtaText,
    RiskChainWrap,
    RiskChainChip,
    RiskChainArrow,
    RiskItem,
    RiskConsequenceBlock,
    RiskAppliedNote,
    ResourceRow,
    FuelSuffix,
} from './SinglePlan.styled'
import { Button } from 'components/ui/Button/Button'
import { Container, ContainerHeader, ContainerContent } from 'components/Containers/Containers'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import {
    activateOperationPlan,
    cancelOperationPlan,
    completeOperationPlan,
    getOperationPlanDashboard,
    getSingleOperationPlan,
    pauseOperationPlan,
    resumeOperationPlan,
} from 'features/operations/plansSlice'
import { raiseRequest } from 'features/operations/opsRequestsSlice'
import { phaseStatuses, planStatuses } from '../plansData'
import { DEMO_PLAN_DASHBOARD, DEMO_PLANS } from '../plansDemoData'
import { tokens } from 'styling/tokens'
import { IOpsLiveTask, IOpsMachineStatus, IOpsRisk, IOpsWorkerStatus } from 'types/operationsControl.interface'
import { IStatusPill } from 'types/ui/statusPill.interface'
import { etaLabel, TASK_ENUM_SUBSTATE, taskCoreState, USE_OPS_MOCKS } from 'pages/Operations/Shared/opsControl'
import { OPS_LIVE_TASKS, OPS_MACHINES, OPS_RISKS, OPS_SCHEDULER, OPS_WORKERS } from 'pages/Operations/Shared/opsControlMocks'
import { CoreStatePill, DemoBadge, SchedulerBanner, SourceBadge } from 'pages/Operations/Shared/OpsSharedComponents'

interface IPlanMenuAction {
    label: string
    danger?: boolean
    onClick: () => void
}

const WORKER_STATE_TONE: { [k in IOpsWorkerStatus['state']]: IStatusPill['status'] } = {
    active: 'success',
    idle: 'warning',
    break: 'neutral',
}

const MACHINE_STATE_TONE: { [k in IOpsMachineStatus['state']]: IStatusPill['status'] } = {
    active: 'success',
    idle: 'warning',
    maintenance: 'neutral',
    offline: 'danger',
}

const RESOURCE_STATE_LABEL: { [k: string]: string } = {
    active: 'Active',
    idle: 'Idle',
    break: 'Break',
    maintenance: 'Maintenance',
    offline: 'Offline',
}

const LOW_FUEL_PCT = 25

const fuelSuffixLabel = (fuelPct: number): string =>
    fuelPct < LOW_FUEL_PCT ? `${fuelPct}% fuel — refuel queued` : `${fuelPct}% fuel`

const RISK_GOVERNANCE: {
    [k: number]: { toDept: string; title: string; consequence: string; neededBy: string; ackName: string; sentToast: string }
} = {
    1: {
        toDept: 'mining',
        title: 'Truck 07 rebooking — approval needed',
        consequence:
            'Truck 07 leaves Waste Stripping for ~2 cycles (~2h). Waste Stripping pace drops to ~71% for that window. The Waste Stripping owner gets a request to acknowledge.',
        neededBy: '08:30',
        ackName: 'Waste Stripping',
        sentToast: 'Sent — Waste Stripping must acknowledge before the rebooking runs',
    },
    2: {
        toDept: 'maintenance',
        title: 'MECH-2 cover for Generator G2 — approval needed',
        consequence:
            'Generator G2 service runs on fatigue-checked overtime (~3h at overtime rates, est. $840) or a MECH-2 fitter is reassigned from the Workshop queue, pushing workshop jobs back one slot. The Maintenance owner gets a request to acknowledge.',
        neededBy: '09:00',
        ackName: 'Maintenance',
        sentToast: 'Sent — Maintenance must acknowledge before the change runs',
    },
}

const WORKER_COUNTS = {
    active: OPS_WORKERS.filter((w) => w.state === 'active').length,
    idle: OPS_WORKERS.filter((w) => w.state === 'idle').length,
    onBreak: OPS_WORKERS.filter((w) => w.state === 'break').length,
}

const MACHINE_COUNTS = {
    active: OPS_MACHINES.filter((m) => m.state === 'active').length,
    maintenance: OPS_MACHINES.filter((m) => m.state === 'maintenance').length,
    offline: OPS_MACHINES.filter((m) => m.state === 'offline').length,
}

export const SinglePlan = () => {
    const [showActions, setShowActions] = useState(false)
    const [previewRiskId, setPreviewRiskId] = useState<number | null>(null)
    const [appliedRisks, setAppliedRisks] = useState<{ [k: number]: boolean }>({})
    const menuRef = useRef<HTMLDivElement>(null)
    const { id } = useParams()
    const navigate = useNavigate()
    const { dashboard: realDashboard, singlePlan } = useSelector((state: RootState) => state.operationPlans)
    const isDemo = !realDashboard && USE_OPS_MOCKS
    const dashboard = realDashboard ?? (USE_OPS_MOCKS ? DEMO_PLAN_DASHBOARD : null)
    const planStatus = dashboard?.status ?? singlePlan?.status
    const dispatch = useDispatch<AppDispatch>()
    const planIdNum = Number(id)

    useEffect(() => {
        dispatch(getSingleOperationPlan(id))
        dispatch(getOperationPlanDashboard(id))
    }, [id])

    const liveTasks = useMemo(() => {
        const matched = OPS_LIVE_TASKS.filter((t) => t.planId === planIdNum)
        return matched.length ? matched : OPS_LIVE_TASKS.filter((t) => t.planId === 1)
    }, [planIdNum])

    const planRisks = useMemo(() => {
        const matched = OPS_RISKS.filter((r) => r.planId === planIdNum)
        return matched.length ? matched : OPS_RISKS.filter((r) => r.planId === 1)
    }, [planIdNum])

    const handleActivate = () => {
        dispatch(activateOperationPlan({ id: planIdNum, single: true }))
    }

    const handleEmergencyGenerate = () => {
        toast.success('Emergency generation queued for this plan')
    }

    const handleConfirmRecommendation = (risk: IOpsRisk) => {
        const governance = RISK_GOVERNANCE[risk.id]
        if (!governance) return
        dispatch(
            raiseRequest({
                fromDept: 'mining',
                toDept: governance.toDept,
                title: governance.title,
                description: `${risk.recommendation}. ${governance.consequence}`,
                linkedEntityLabel: dashboard?.name ?? singlePlan?.name ?? null,
                linkedPath: '/operations/plans/2',
                priority: 'high',
                neededBy: governance.neededBy,
                createdBy: 'You (North Pit Extraction)',
            }),
        )
        toast.success(governance.sentToast)
        setAppliedRisks((prev) => ({ ...prev, [risk.id]: true }))
        setPreviewRiskId(null)
    }

    const costActual = dashboard?.costPerUnit || null
    const costPlanned = (isDemo ? DEMO_PLANS.find((p) => p.id === dashboard?.id)?.plannedCostPerUnit : null) ?? null
    const costSublabel =
        costPlanned != null
            ? `plan $${costPlanned.toFixed(2)}${dashboard?.budgetRemaining ? ` · $${dashboard.budgetRemaining.toLocaleString('en-US')} budget left` : ''}`
            : undefined

    const actionItems = useMemo((): IPlanMenuAction[] => {
        const runAction = (action: typeof cancelOperationPlan) => () => {
            setShowActions(false)
            dispatch(action({ id: Number(id), single: true }))
        }

        const items: IPlanMenuAction[] = [
            {
                label: 'Duplicate',
                onClick: () => {
                    setShowActions(false)
                    navigate('duplicate')
                },
            },
            {
                label: 'Audit log',
                onClick: () => {
                    setShowActions(false)
                    navigate('audit')
                },
            },
        ]

        const cancelItem: IPlanMenuAction = {
            label: 'Cancel plan',
            danger: true,
            onClick: runAction(cancelOperationPlan),
        }

        if (planStatus === 1) {
            items.push({ label: 'Activate', onClick: runAction(activateOperationPlan) })
            items.push(cancelItem)
            items.push({ label: 'Edit', onClick: () => navigate('edit') })
        } else if (planStatus === 2) {
            items.push(
                { label: 'Pause', onClick: runAction(pauseOperationPlan) },
                { label: 'Complete', onClick: runAction(completeOperationPlan) },
                cancelItem,
            )
        } else if (planStatus === 3) {
            items.push(
                { label: 'Resume', onClick: runAction(resumeOperationPlan) },
                cancelItem,
            )
        }

        return items
    }, [planStatus, id])

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                setShowActions(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const getPhaseExecutable = (phase) => {
        if (phase.executableType == 1) {
            return phase.taskCycleTemplate?.name
        } else if (phase.executableType == 2) {
            return phase.workflowTemplate?.name
        } else {
            return phase.processTemplate?.name
        }
    }

    const taskColumns = useMemo((): IHonestTableColumn<IOpsLiveTask>[] => [
        {
            id: 'task',
            header: 'Task',
            minWidth: '16rem',
            cell: (t) => (
                <Flex column gap='0.25rem'>
                    <BodyText style={{ margin: 0 }}>{t.name}</BodyText>
                    {t.blockedReason && <OtherText color={tokens.status.danger.fg}>{t.blockedReason}</OtherText>}
                    {t.unassignedReason && <OtherText color={tokens.status.warning.fg}>{t.unassignedReason}</OtherText>}
                </Flex>
            ),
        },
        { id: 'machine', header: 'Machine', cell: (t) => t.machine ?? '—' },
        { id: 'operator', header: 'Operator', cell: (t) => t.operator ?? '—' },
        {
            id: 'status',
            header: 'Status',
            cell: (t) => (
                <Flex column gap='0.25rem' alignStart>
                    <CoreStatePill state={taskCoreState(t.taskStatus)} context='task' />
                    <OtherText grey>{TASK_ENUM_SUBSTATE[t.taskStatus]}</OtherText>
                </Flex>
            ),
        },
        { id: 'source', header: 'Source', cell: (t) => <SourceBadge source={t.source} /> },
        { id: 'eta', header: 'ETA', cell: (t) => <EtaText>{etaLabel(t.etaMin)}</EtaText> },
        { id: 'location', header: 'Location', cell: (t) => t.location },
    ], [])

    return <>
        <NavBar />
        <PageWrapper>
            <Flex center gap='1rem'>
                <H1WithBack title={dashboard?.name ?? singlePlan?.name} arrow />
                <DemoBadge />
                <StatusPill status={planStatus === 5 ? 'danger' : planStatus === 2 || planStatus === 4 ? 'success' : 'accent'}>{planStatuses.find((status) => status.value === planStatus)?.label}</StatusPill>
                <BodyText grey>{dashboard?.code ?? singlePlan?.code}</BodyText>
                <ActionsMenuWrapper ref={menuRef}>
                    <Button variant='secondary' onClick={() => setShowActions((p) => !p)}>
                        <KebabIcon>
                            <span />
                            <span />
                            <span />
                        </KebabIcon>
                    </Button>
                    <ActionsDropdown show={showActions}>
                        {actionItems.map((item) => (
                            <ActionsDropdownItem key={item.label} $danger={item.danger} onClick={item.onClick}>
                                <OtherText>{item.label}</OtherText>
                            </ActionsDropdownItem>
                        ))}
                    </ActionsDropdown>
                </ActionsMenuWrapper>
            </Flex>
            <Flex width='100%' mt='16'>
                <SchedulerBanner scheduler={OPS_SCHEDULER} onEmergencyGenerate={handleEmergencyGenerate} />
            </Flex>
            {!dashboard ? (
                <Flex width='100%' mt='32'>
                    <Container>
                        <ContainerContent>
                            {planStatus === 1 ? (
                                <Flex column center justcenter gap='1rem' height='15rem' width='100%'>
                                    <TitleText>No active tasks for this plan. Work is created automatically once the plan is activated.</TitleText>
                                    <Button onClick={handleActivate}>Activate plan</Button>
                                </Flex>
                            ) : (
                                <NoData text='No live data for this plan yet.' />
                            )}
                        </ContainerContent>
                    </Container>
                </Flex>
            ) : (
                <>
                    <Flex gap='1rem' style={{ margin: '2rem 0' }}>
                        <MetricCard value={dashboard?.tasksGeneratedCount} label='Tasks created' accentColor={tokens.accent.base} />
                        <MetricCard value={dashboard?.tasksCompletedCount} label='Completed' accentColor={tokens.accent.base} />
                        <MetricCard value={dashboard?.queueDepth} label='Waiting to start' accentColor={tokens.accent.base} />
                        <MetricCard value={isDemo ? '+2.4% ahead' : 'N / A'} label='vs plan' accentColor={tokens.accent.base} />
                        <MetricCard
                            value={costActual != null ? `$${costActual.toFixed(2)}` : 'N / A'}
                            label='Cost per tonne'
                            sublabel={costSublabel}
                            trailing={
                                costActual != null && costPlanned != null ? (
                                    <StatusPill status={costActual > costPlanned ? 'danger' : 'success'}>
                                        {costActual > costPlanned ? 'over plan' : 'under plan'}
                                    </StatusPill>
                                ) : undefined
                            }
                            accentColor={tokens.accent.base}
                        />
                        {USE_OPS_MOCKS && dashboard?.fuelCostToDate != null && (
                            <MetricCard
                                value={`GH₵ ${(dashboard.fuelCostToDate / 1000).toFixed(1)}k`}
                                label='Fuel'
                                sublabel={`${(dashboard.fuelLitresToDate ?? 0).toLocaleString('en-US')} L to date · attributed from dispense records`}
                                accentColor={tokens.accent.base}
                            />
                        )}
                        <MetricCard value={dashboard?.workersActiveCount} label='Workers active' accentColor={tokens.accent.base} />
                        <MetricCard value={isDemo ? '7' : 'N / A'} label='Automations fired' accentColor={tokens.accent.base} />
                    </Flex>
                    <Flex gap='1rem' width='100%' mb='16'>
                        <Flex column gap='1rem' width='70%'>
                            <Container>
                                <ContainerHeader>
                                    <Flex between center width='100%'>
                                        <TitleText weight='bold'>Running now</TitleText>
                                        <OtherText grey>{liveTasks.length} live tasks</OtherText>
                                    </Flex>
                                </ContainerHeader>
                                <ContainerContent>
                                    <HonestTable
                                        columns={taskColumns}
                                        data={liveTasks}
                                        keyField='id'
                                        onClick={(t) => navigate(`/operations/task/${t.id}`)}
                                        emptyMessage='No active tasks for this plan.'
                                    />
                                </ContainerContent>
                            </Container>
                        </Flex>
                        <Flex width='30%' column gap='1rem'>
                            <Container>
                                <ContainerHeader>
                                    <TitleText weight='bold'>Risks</TitleText>
                                </ContainerHeader>
                                <ContainerContent>
                                    {planRisks.length ? (
                                        <Flex column gap='1rem'>
                                            {planRisks.map((risk) => {
                                                const governance = RISK_GOVERNANCE[risk.id]
                                                return <RiskItem key={risk.id}>
                                                    <RiskChainWrap>
                                                        {risk.chain.map((step, i) => (
                                                            <Fragment key={step}>
                                                                {i > 0 && <RiskChainArrow>→</RiskChainArrow>}
                                                                <RiskChainChip>{step}</RiskChainChip>
                                                            </Fragment>
                                                        ))}
                                                    </RiskChainWrap>
                                                    <Flex column gap='0.25rem'>
                                                        <OtherText grey>ROOT CAUSE</OtherText>
                                                        <BodyText style={{ margin: 0 }}>{risk.rootCause}</BodyText>
                                                    </Flex>
                                                    <Flex column gap='0.25rem'>
                                                        <OtherText grey>IMPACT</OtherText>
                                                        <BodyText style={{ margin: 0 }}>{risk.impact}</BodyText>
                                                    </Flex>
                                                    <Flex column gap='0.25rem'>
                                                        <OtherText grey>ETA</OtherText>
                                                        <BodyText style={{ margin: 0 }}>{risk.eta}</BodyText>
                                                    </Flex>
                                                    <Flex column gap='0.5rem'>
                                                        <OtherText grey>RECOMMENDATION</OtherText>
                                                        <BodyText style={{ margin: 0 }}>{risk.recommendation}</BodyText>
                                                        {governance && appliedRisks[risk.id] && (
                                                            <RiskAppliedNote>
                                                                <OtherText color={tokens.status.success.fg}>
                                                                    {`✓ Applied by you · 07:55 · pending ${governance.ackName} acknowledgement`}
                                                                </OtherText>
                                                                <OtherText grey>Logged to the audit trail once connected.</OtherText>
                                                            </RiskAppliedNote>
                                                        )}
                                                        {governance && !appliedRisks[risk.id] && previewRiskId === risk.id && (
                                                            <RiskConsequenceBlock>
                                                                <OtherText grey>WHAT HAPPENS</OtherText>
                                                                <BodyText style={{ margin: 0 }}>{governance.consequence}</BodyText>
                                                                <Flex gap='0.5rem'>
                                                                    <Button variant='primary' size='sm' onClick={() => handleConfirmRecommendation(risk)}>
                                                                        Confirm & notify owner
                                                                    </Button>
                                                                    <Button variant='black' size='sm' onClick={() => setPreviewRiskId(null)}>
                                                                        Back
                                                                    </Button>
                                                                </Flex>
                                                            </RiskConsequenceBlock>
                                                        )}
                                                        {governance && !appliedRisks[risk.id] && previewRiskId !== risk.id && (
                                                            <Flex>
                                                                <Button variant='black' size='sm' onClick={() => setPreviewRiskId(risk.id)}>
                                                                    Apply recommendation
                                                                </Button>
                                                            </Flex>
                                                        )}
                                                    </Flex>
                                                </RiskItem>
                                            })}
                                        </Flex>
                                    ) : (
                                        <BodyText grey style={{ margin: 0 }}>No active risks for this plan.</BodyText>
                                    )}
                                </ContainerContent>
                            </Container>
                            <Container>
                                <ContainerHeader>
                                    <TitleText weight='bold'>Resource status</TitleText>
                                </ContainerHeader>
                                <ContainerContent>
                                    <Flex column gap='0.75rem'>
                                        <Flex column gap='0.25rem'>
                                            <OtherText grey>{`Workers: ${WORKER_COUNTS.active} active · ${WORKER_COUNTS.idle} idle · ${WORKER_COUNTS.onBreak} break`}</OtherText>
                                            <OtherText grey>{`Machines: ${MACHINE_COUNTS.active} active · ${MACHINE_COUNTS.maintenance} maintenance · ${MACHINE_COUNTS.offline} offline`}</OtherText>
                                        </Flex>
                                        <Flex column gap='0.25rem'>
                                            {OPS_WORKERS.map((worker) => (
                                                <ResourceRow key={`w-${worker.id}`} $idle={worker.state === 'idle'}>
                                                    <Flex column gap='0.125rem'>
                                                        <BodyText style={{ margin: 0 }}>{worker.name}</BodyText>
                                                        <OtherText grey>{worker.taskName ? `${worker.role} · ${worker.taskName}` : worker.role}</OtherText>
                                                    </Flex>
                                                    <StatusPill status={WORKER_STATE_TONE[worker.state]}>{RESOURCE_STATE_LABEL[worker.state]}</StatusPill>
                                                </ResourceRow>
                                            ))}
                                            {OPS_MACHINES.map((machine) => (
                                                <ResourceRow key={`m-${machine.id}`}>
                                                    <Flex column gap='0.125rem'>
                                                        <BodyText style={{ margin: 0 }}>
                                                            {machine.name}
                                                            {machine.fuelPct !== undefined && (
                                                                <FuelSuffix $low={machine.fuelPct < LOW_FUEL_PCT}>
                                                                    {fuelSuffixLabel(machine.fuelPct)}
                                                                </FuelSuffix>
                                                            )}
                                                        </BodyText>
                                                        <OtherText grey>{machine.taskName ?? 'No task'}</OtherText>
                                                    </Flex>
                                                    <StatusPill status={MACHINE_STATE_TONE[machine.state]}>{RESOURCE_STATE_LABEL[machine.state]}</StatusPill>
                                                </ResourceRow>
                                            ))}
                                        </Flex>
                                    </Flex>
                                </ContainerContent>
                            </Container>
                        </Flex>
                    </Flex>
                    <Flex gap='1rem' width='100%' >
                        <Flex column gap='1rem' width='70%'>
                            <Container>
                                <ContainerHeader>
                                    <Flex between center width='100%'>
                                        <TitleText weight='bold'>Phases</TitleText>
                                    </Flex>
                                </ContainerHeader>
                                <ContainerContent>
                                    <PhaseList>
                                        {dashboard?.phases?.map((phase) => (
                                            <PhaseItem key={phase.id}>
                                                <PhaseIndicator>
                                                    <PhaseDot />
                                                </PhaseIndicator>
                                                <Flex column gap='0.25rem' width='100%'>
                                                    <Flex center gap='0.5rem'>
                                                        <Flex column gap='0.25rem' mr='16' style={{ width: '50%' }} >
                                                            <TitleText weight='bold'>{phase.name}</TitleText>
                                                            <TitleText>{getPhaseExecutable(phase)}</TitleText>
                                                            <OtherText grey>{phase.executableType == 1 ? 'Task Cycle' : phase.executableType == 2 ? 'Workflow' : 'Process'}</OtherText>
                                                        </Flex>


                                                        <Flex width='100%' justend >
                                                            <StatusPill status={phase.status === 4 ? 'danger' : phase.status === 2 || phase.status === 3 ? 'success' : 'accent'}>{phaseStatuses.find((status) => status.value === phase.status)?.label}</StatusPill>
                                                        </Flex>

                                                    </Flex>
                                                </Flex>
                                            </PhaseItem>
                                        ))}
                                    </PhaseList>
                                </ContainerContent>
                            </Container>

                            <Container>
                                <ContainerHeader>
                                    <Flex between center width='100%'>
                                        <TitleText weight='bold'>Upcoming work</TitleText>
                                        <Button variant='secondary' size='sm' onClick={() => navigate(`/operations/plans/${id}/horizon`)}>See upcoming work</Button>
                                    </Flex>
                                </ContainerHeader>
                                <ContainerContent>
                                    <Flex width='100%' >
                                        <Flex column gap='0.2rem' width='100%' >
                                            <OtherText grey>TYPE</OtherText>
                                            <TitleText>{dashboard?.horizon?.horizonType == 1 ? 'Looks ahead 6 hours' : 'Looks ahead one shift'} </TitleText>
                                        </Flex>
                                        <Flex column gap='0.2rem' width='100%' >
                                            <OtherText grey>TOP-UP</OtherText>
                                            <TitleText>{dashboard?.horizon?.replenishThreshold != null ? `Tops up when fewer than ${dashboard?.horizon?.replenishThreshold} queued` : 'N / A'} </TitleText>
                                        </Flex>
                                        <Flex column gap='0.2rem' width='100%' >
                                            <OtherText grey>HOW OFTEN</OtherText>
                                            <TitleText>{dashboard?.horizon?.schedulerRunFreqMin ? `Checks every ${dashboard?.horizon?.schedulerRunFreqMin} min` : 'N / A'} </TitleText>
                                        </Flex>
                                        <Flex column gap='0.2rem' width='100%' >
                                            <OtherText grey>QUEUE HEALTH</OtherText>
                                            <TitleText>{dashboard?.horizon?.queueHealth == 1 ? 'On Track' : 'Behind'} </TitleText>
                                        </Flex>
                                    </Flex>
                                </ContainerContent>
                            </Container>
                        </Flex>
                        <Flex width='30%' column gap='1rem' >
                            <Container>
                                <ContainerHeader>
                                    <TitleText weight='bold'>Alerts · last 24h</TitleText>
                                </ContainerHeader>
                                <ContainerContent>
                                    <Flex column gap='0.75rem'>
                                        <Flex column gap='0.25rem'>
                                            <BodyText style={{ margin: 0 }}>Phase delay — Haulage</BodyText>
                                            <OtherText grey>Queue behind by 12 min · 2h ago</OtherText>
                                        </Flex>
                                        <Flex column gap='0.25rem'>
                                            <BodyText style={{ margin: 0 }}>Queue ran low — topped up</BodyText>
                                            <OtherText grey>Upcoming work refreshed · 5h ago</OtherText>
                                        </Flex>
                                    </Flex>
                                </ContainerContent>
                            </Container>

                            <Container>
                                <ContainerHeader>
                                    <TitleText weight='bold'>Queue health by department
                                    </TitleText>
                                </ContainerHeader>
                                <ContainerContent>
                                    <Flex column gap='0.75rem'>
                                        {[
                                            { dept: 'Mining Ops', status: 'On Track', behind: 0 },
                                            { dept: 'Haulage', status: 'Behind', behind: 3 },
                                            { dept: 'Processing', status: 'On Track', behind: 0 },
                                            { dept: 'Maintenance', status: 'Behind', behind: 1 },
                                        ].map((row) => (
                                            <Flex key={row.dept} between center width='100%'>
                                                <BodyText style={{ margin: 0 }}>{row.dept}</BodyText>
                                                <Flex center gap='0.5rem'>
                                                    <ColoredDot color={row.status === 'On Track' ? tokens.status.success.fg : tokens.accent.base} />
                                                    <OtherText grey style={{ margin: 0 }}>
                                                        {row.status}{row.behind > 0 ? ` · ${row.behind} tasks` : ''}
                                                    </OtherText>
                                                </Flex>
                                            </Flex>
                                        ))}
                                    </Flex>
                                </ContainerContent>
                            </Container>
                        </Flex>

                    </Flex>
                </>
            )}
        </PageWrapper>
    </>
}
